import { AsrMethod, IconState } from "enums/AdminPageEnums";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  FormikAddressField,
  Card,
  FormikSelect,
  FormikTextField,
  Icon,
  PageHeader,
  SaveBar,
  TextField,
  Toggle,
  useToast,
} from "stories/Admin";
import { AdminFormValues, MosqueInfo } from "types/AdminPageTypes";
import * as Yup from "yup";
import { ReactComponent as DefaultLogo } from "../../assets/default-logo.svg";
import { useAuth } from "../../AuthProvider";
import useFetch from "../../hooks/useFetch";
import styles from "./AdminPage.module.scss";
import AdminPageSkeleton from "./AdminPageSkeleton/AdminPageSkeleton";
import { formatOrgNumber } from "../AdminLayout/menuConfig";
import icons from "./IconComponents";

const validationSchema = Yup.object({
  orgNr: Yup.string(),
  orgName: Yup.string(),
  name: Yup.string().nullable(),
  streetAddress: Yup.string(),
  contactName: Yup.string(),
  contactEmail: Yup.string().nullable(),
  contactPhone: Yup.string().optional(),
  merchantId: Yup.string().nullable().default(""),
  postalCode: Yup.string(),
  city: Yup.string(),
  prayerTimeTable: Yup.number(),
});

const AdminPage: React.FC = () => {
  const { execute, data, error, isLoading } = useFetch<AdminFormValues>();
  const { execute: fetchMosqueInfo, data: mosqueInfoData } =
    useFetch<MosqueInfo>();
  const [mosqueInfo, setMosqueInfo] = useState<MosqueInfo | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [initialFormValues, setInitialFormValues] =
    useState<AdminFormValues | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [iconStates, setIconStates] = useState<{ [key: string]: boolean }>({});
  const [savedIconStates, setSavedIconStates] = useState<{
    [key: string]: boolean;
  }>({});
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const {
    setLocationId,
    setPrayerTimeTableId,
    selectedOrgNumber,
    selectedOrgName,
    mosqueName,
    setMosqueName,
    setMosqueLogo,
  } = useAuth();

  const [prayerTimeTables, setPrayerTimeTables] = useState<
    Array<{ id: number; name: string }>
  >([]);

  const applyIconStates = (states: { [key: string]: boolean }) => {
    setIconStates(states);
    setSavedIconStates(states);
  };

  useEffect(() => {
    const fetchData = async () => {
      setMosqueInfo(null);
      setInitialFormValues(null);
      setLogoUrl(null);
      setIconStates({});
      setSavedIconStates({});
      setSelectedFile(null);

      const mosqueResult = await execute(
        `/api/v1/mosques/${selectedOrgNumber}`,
        "GET",
      );
      const mosqueInfoResult = await fetchMosqueInfo(
        `/api/v1/mosqueInfo/org/${selectedOrgNumber}`,
        "GET",
      );

      if (mosqueResult) {
        const logo = mosqueResult.logo || null;
        setLogoUrl(logo);
        setSavedLogoUrl(logo);

        if (logo) {
          sessionStorage.setItem("mosqueLogo", logo);
          setMosqueLogo(logo);
        }

        setInitialFormValues({
          ...mosqueResult,
          menCapacity: mosqueInfoResult?.capacityMen ?? 100,
          womenCapacity: mosqueInfoResult?.capacityWomen ?? 100,
          asrMethod: mosqueResult.asrMethod || "NONE",
          prayerTimeTable: mosqueResult.prayerTimeTable || 0,
        });

        setMosqueName(mosqueResult.name);
        sessionStorage.setItem("mosqueName", mosqueResult.name);

        if (mosqueResult.locationId) {
          setLocationId(mosqueResult.locationId);

          const locationData = await execute(
            `/api/v1/locations/${mosqueResult.locationId}`,
            "GET",
          );

          if (locationData && locationData.prayerTimesTables) {
            setPrayerTimeTables(locationData.prayerTimesTables);
          }

          if (mosqueResult.prayerTimeTable) {
            setPrayerTimeTableId(mosqueResult.prayerTimeTable);
            sessionStorage.setItem(
              "prayerTimeTableId",
              mosqueResult.prayerTimeTable.toString(),
            );
          }
        }
      }

      if (mosqueInfoResult) {
        setMosqueInfo(mosqueInfoResult);
        applyIconStates({
          ablutionMen: mosqueInfoResult.ablutionMen,
          ablutionWomen: mosqueInfoResult.ablutionWomen,
          hallHire: mosqueInfoResult.hallHire,
          iftar: mosqueInfoResult.iftar,
          nikah: mosqueInfoResult.nikah,
          janazah: mosqueInfoResult.janazah,
          advice: mosqueInfoResult.advice,
          newMuslim: mosqueInfoResult.newMuslim,
          jummah: mosqueInfoResult.jummah,
          taraweeh: mosqueInfoResult.taraweeh,
          eid: mosqueInfoResult.eid,
          parking: mosqueInfoResult.parking,
          madrassahAdult: mosqueInfoResult.madrassahAdult,
          madrassahChildren: mosqueInfoResult.madrassahChildren,
          kitchen: mosqueInfoResult.kitchen,
          toiletMen: mosqueInfoResult.toiletMen,
          toiletWomen: mosqueInfoResult.toiletWomen,
          darsWomen: mosqueInfoResult.darsWomen,
        });
      } else {
        applyIconStates({
          ablutionMen: true,
          ablutionWomen: true,
          hallHire: true,
          iftar: true,
          nikah: true,
          janazah: true,
          advice: true,
          newMuslim: true,
          jummah: true,
          taraweeh: true,
          eid: true,
          parking: true,
          madrassahAdult: true,
          madrassahChildren: true,
          kitchen: true,
          toiletMen: true,
          toiletWomen: true,
          darsWomen: true,
        });
      }
    };

    fetchData();
  }, [selectedOrgNumber]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);

      const reader = new FileReader();
      reader.onload = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const handleLogoUpload = async (): Promise<string | null> => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      try {
        const result = await execute(
          `/api/v1/files/upload/LOGO/${selectedOrgNumber}`,
          "POST",
          formData,
        );

        if (result && result.filePath) {
          return result.filePath;
        } else {
          toast.error("Noe gikk galt under opplasting av logoen.");
          return null;
        }
      } catch (error) {
        toast.error("Noe gikk galt under opplasting av logoen.");
        return null;
      }
    }
    return null;
  };

  const iconStateMapping: { [key: string]: IconState } = {
    "Barne Madrassa": IconState.MadrassahChildren,
    Rådgivning: IconState.Advice,
    "Kurs for kvinner": IconState.DarsWomen,
    Eid: IconState.Eid,
    "Leie hall": IconState.HallHire,
    Janazah: IconState.Janazah,
    "Jummah bønn": IconState.Jummah,
    Kjøkken: IconState.Kitchen,
    "Nikkah tjenester": IconState.Nikah,
    Parkering: IconState.Parking,
    "Ramadan iftar": IconState.Iftar,
    "Støtte ny-muslimer": IconState.NewMuslim,
    Taraweeh: IconState.Taraweeh,
    "Toaletter Menn": IconState.ToiletMen,
    "Toaletter Kvinner": IconState.ToiletWomen,
    "Voksen Madrassa": IconState.MadrassahAdult,
    "Wudu Menn": IconState.AblutionMen,
    "Wudu Kvinner": IconState.AblutionWomen,
  };

  const handleSubmit = async (values: AdminFormValues) => {
    const payload = {
      orgNr: values.orgNr,
      name: values.name,
      orgName: values.orgName,
      streetAddress: values.streetAddress,
      postalCode: values.postalCode,
      city: values.city,
      lat: values.lat ?? undefined,
      lon: values.lon ?? undefined,
      contactName: values.contactName,
      contactPhone: values.contactPhone,
      merchantId: values.merchantId,
      contactEmail: values.contactEmail,
      asrMethod: values.asrMethod,
      prayerTimeTable: values.prayerTimeTable,
    };

    const mosqueInfoPayload = {
      capacityMen: values.menCapacity,
      capacityWomen: values.womenCapacity,
      ablutionMen: iconStates.ablutionMen,
      ablutionWomen: iconStates.ablutionWomen,
      hallHire: iconStates.hallHire,
      iftar: iconStates.iftar,
      nikah: iconStates.nikah,
      janazah: iconStates.janazah,
      advice: iconStates.advice,
      newMuslim: iconStates.newMuslim,
      jummah: iconStates.jummah,
      taraweeh: iconStates.taraweeh,
      eid: iconStates.eid,
      parking: iconStates.parking,
      madrassahAdult: iconStates.madrassahAdult,
      madrassahChildren: iconStates.madrassahChildren,
      kitchen: iconStates.kitchen,
      toiletMen: iconStates.toiletMen,
      toiletWomen: iconStates.toiletWomen,
      mosqueOrgNr: selectedOrgNumber,
      darsWomen: iconStates.darsWomen,
    };

    try {
      const result = await execute(
        `/api/v1/mosques/${selectedOrgNumber}`,
        "PATCH",
        payload,
      );

      if (mosqueInfo?.id) {
        await execute(
          `/api/v1/mosqueInfo/${mosqueInfo.id}`,
          "PATCH",
          mosqueInfoPayload,
        );
      } else {
        await execute(`/api/v1/mosqueInfo`, "POST", mosqueInfoPayload);
      }

      if (result) {
        let newLogoUrl = logoUrl;
        if (selectedFile) {
          newLogoUrl = await handleLogoUpload();
        }

        setInitialFormValues({
          ...values,
          logo: newLogoUrl || values.logo,
        });

        setMosqueName(values.name);
        if (newLogoUrl) {
          setMosqueLogo(newLogoUrl);
          sessionStorage.setItem("mosqueLogo", newLogoUrl);
        }

        sessionStorage.setItem("mosqueName", values.name);

        setSelectedFile(null);
        setLogoUrl(newLogoUrl);
        setSavedLogoUrl(newLogoUrl);
        setSavedIconStates(iconStates);
        toast.success("Endringene er lagret.");
      } else {
        toast.error("Kunne ikke lagre opplysningene om moskeen. Prøv igjen.");
      }
    } catch (error) {
      toast.error("Noe gikk galt under lagringen. Prøv igjen.");
    }
  };

  const handleToggle = (key: string) => {
    setIconStates((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const discardLocalChanges = () => {
    setIconStates(savedIconStates);
    setSelectedFile(null);
    setLogoUrl(savedLogoUrl);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const iconsChanged = Object.keys(iconStates).some(
    (key) => iconStates[key] !== savedIconStates[key],
  );
  const toggleIcons = icons.filter((icon) => icon.type === "toggle");
  const enabledCount = toggleIcons.filter(
    (icon) => iconStates[iconStateMapping[icon.name]],
  ).length;
  const crumb = mosqueName || selectedOrgName || undefined;

  if (!initialFormValues) {
    if (isLoading) {
      return <AdminPageSkeleton />;
    }

    return (
      <>
        <PageHeader crumb={crumb} title="Moské-info" />
        <Alert tone="error">
          {error
            ? "Kunne ikke hente opplysningene om moskeen. Last siden på nytt, eller prøv igjen senere."
            : "Ingen data tilgjengelig for valgt moské."}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        crumb={crumb}
        title="Moské-info"
        subtitle="Opplysningene vises på moskeens side i iBønn-appen."
      />
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ dirty, isSubmitting, resetForm, values, setFieldValue }) => (
          <Form className={styles.form} noValidate>
            <div className={styles.columns}>
              <div className={styles.column}>
                <Card title="Moskeen">
                  <div className={styles.mosqueRow}>
                    <div className={styles.logoCol}>
                      <span className={styles.logoBox}>
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt="Moskeens logo"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <DefaultLogo aria-hidden="true" />
                        )}
                      </span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        id="logoUpload"
                        className={styles.fileInput}
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        icon={<Icon name="upload" size={14} strokeWidth={2} />}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Bytt logo
                      </Button>
                      <span className={styles.help}>
                        {selectedFile ? selectedFile.name : "PNG eller JPG"}
                      </span>
                    </div>
                    <div className={styles.mosqueFields}>
                      <FormikTextField name="name" label="Navn i appen" />
                      <div className={styles.addressGrid}>
                        <FormikAddressField
                          name="streetAddress"
                          label="Adresse"
                          help={
                            values.lat != null && values.lon != null
                              ? `Koordinater: ${values.lat}, ${values.lon}`
                              : "Velg et forslag for å fylle inn postnr., sted og koordinater."
                          }
                          onPick={(a) => {
                            if (a.postalCode)
                              setFieldValue("postalCode", a.postalCode);
                            if (a.city) setFieldValue("city", a.city);
                            setFieldValue("lat", a.lat);
                            setFieldValue("lon", a.lon);
                          }}
                        />
                        <div className={styles.postGrid}>
                          <FormikTextField name="postalCode" label="Postnr." />
                          <FormikTextField name="city" label="Sted" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card
                  title="Kontaktperson"
                  subtitle="Vises ikke i appen. Brukes når iBønn trenger å nå moskeen."
                >
                  <div className={styles.contactGrid}>
                    <FormikTextField name="contactName" label="Navn" />
                    <FormikTextField
                      name="contactEmail"
                      label="E-post"
                      type="email"
                    />
                    <FormikTextField
                      name="contactPhone"
                      label="Mobilnummer"
                      type="number"
                      className={styles.noSpin}
                    />
                  </div>
                </Card>
              </div>

              <div className={styles.column}>
                <Card title="Organisasjon">
                  <TextField
                    label="Organisasjonsnummer"
                    value={formatOrgNumber(initialFormValues.orgNr)}
                    readOnly
                  />
                  <FormikTextField
                    name="orgName"
                    label="Organisasjonsnavn"
                    readOnly
                  />
                  <span className={styles.help}>
                    Endres av iBønn. Ta kontakt hvis noe er feil.
                  </span>
                </Card>

                <Card title="Bønnetider">
                  <FormikSelect
                    name="prayerTimeTable"
                    label="Bønnetabell"
                    help="Tabellene som finnes for moskeens lokasjon."
                  >
                    {prayerTimeTables.map((table) => (
                      <option key={table.id} value={table.id}>
                        {table.name}
                      </option>
                    ))}
                  </FormikSelect>
                  <FormikSelect
                    name="asrMethod"
                    label="Asr-metode"
                    help="1× skygge (Shafi'i, Maliki, Hanbali) eller 2× skygge (Hanafi) gjelder hele året. Blandet bruker 1× i sommertid og 2× i vintertid. «Ingen» betyr det samme som blandet."
                  >
                    <option value={AsrMethod.NONE}>Ingen (blandet)</option>
                    <option value={AsrMethod.MIXED}>
                      Blandet (1× sommer, 2× vinter)
                    </option>
                    <option value={AsrMethod.ONE_X}>1× skygge (Shafi'i)</option>
                    <option value={AsrMethod.TWO_X}>2× skygge (Hanafi)</option>
                  </FormikSelect>
                </Card>
                {/* Vipps-lenken på moskeen er fjernet: innsamling skjer via Kampanjer, som har egen Vipps-lenke.
                    merchantId sendes fortsatt uendret, så en lagret verdi ikke slettes ved lagring. */}
              </div>
            </div>

            <Card
              title="Fasiliteter og tjenester"
              subtitle="Slå på det moskeen tilbyr. Ikonene vises på moskeens side i appen."
              actions={
                <span className={styles.help}>
                  {enabledCount} av {toggleIcons.length} på
                </span>
              }
            >
              <div className={styles.capacityGrid}>
                <FormikTextField
                  name="womenCapacity"
                  label="Kapasitet kvinner"
                  type="number"
                  min={0}
                />
                <FormikTextField
                  name="menCapacity"
                  label="Kapasitet menn"
                  type="number"
                  min={0}
                />
              </div>
              <div className={styles.facilityGrid}>
                {toggleIcons.map((icon) => {
                  const key = iconStateMapping[icon.name];
                  const on = !!iconStates[key];
                  return (
                    <div
                      key={key}
                      className={`${styles.facility} ${
                        on ? "" : styles.facilityOff
                      }`}
                    >
                      <icon.component
                        className={styles.facilityIcon}
                        aria-hidden="true"
                      />
                      <Toggle
                        className={styles.facilityToggle}
                        checked={on}
                        onChange={() => handleToggle(key)}
                        label={
                          <span className={styles.facilityLabel}>
                            {facilityLabels[key] ?? icon.name}
                          </span>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </Card>

            <SaveBar
              visible={dirty || iconsChanged || !!selectedFile}
              saving={isSubmitting}
              saveType="submit"
              onDiscard={() => {
                resetForm();
                discardLocalChanges();
              }}
            />
          </Form>
        )}
      </Formik>
    </>
  );
};

/** Labels from the design, keyed by the mosqueInfo field. */
const facilityLabels: { [key: string]: string } = {
  madrassahChildren: "Barne-madrassa",
  advice: "Rådgivning",
  darsWomen: "Kurs for kvinner",
  hallHire: "Leie av hall",
  kitchen: "Kjøkken",
  madrassahAdult: "Voksen-madrassa",
  parking: "Parkering",
  newMuslim: "Støtte til nye muslimer",
  nikah: "Nikah-tjenester",
  taraweeh: "Taraweeh",
  jummah: "Jummah-bønn",
  eid: "Eid",
  iftar: "Ramadan-iftar",
  janazah: "Janazah",
  toiletMen: "Toaletter menn",
  toiletWomen: "Toaletter kvinner",
  ablutionMen: "Wudu menn",
  ablutionWomen: "Wudu kvinner",
};

export default AdminPage;
